import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { withI18next } from 'lib/i18n/withI18next'
import PropTypes from 'prop-types'
import React from 'react'
import AverageStars from 'components/common/average-stars/AverageStars'
import clsx from 'clsx'
import Typo from 'constants/typography'
import { hasLocizeTranslation } from 'lib/utils/locize'
import ReviewImagesModal from 'components/pdp/shared-components/review-images-modal/ReviewImagesModal'
import ReviewCarouselImages from 'components/pdp/shared-components/review-carousel-images/ReviewCarouselImages'
import { RATING_FIT_VALUES_ENUM } from 'components/pdp/const'
import { EVENT } from 'lib/segment/const'
import styles from './product-review-card.scss'

const getRatingFitTitleKey = (t, rating) => {
  switch (rating) {
    case RATING_FIT_VALUES_ENUM.LOOSE:
      return hasLocizeTranslation(t, 'LOOSE', 'Loose')
    case RATING_FIT_VALUES_ENUM.TIGHT:
      return hasLocizeTranslation(t, 'TIGHT', 'Tight')
    case RATING_FIT_VALUES_ENUM.TRUE_TO_SIZE:
      return hasLocizeTranslation(t, 'TRUE_TO_SIZE', 'True to size')
    default:
      return hasLocizeTranslation(t, 'TRUE_TO_SIZE', 'True to size')
  }
}

const ProductReviewCard = ({ t, isPhone, productReview }) => (
  <div
    data-cy="product-review-card-container"
    className="product-review-card-container"
  >
    <style jsx>{styles}</style>
    <div className="product-review-card-container__content">
      <div className="product-review-card-container__content__name-average-imgs-wrapper">
        <div className="product-review-card-container__content__name-average-wrapper">
          {isPhone && (
            <div className={clsx(Typo.body2)}>{productReview?.firstname}</div>
          )}
          {productReview?.average_rating && (
            <AverageStars averageRating={productReview?.average_rating} />
          )}
          {!isPhone && (
            <div className={clsx(Typo.body2)}>{productReview?.firstname}</div>
          )}
        </div>
        {productReview?.images?.length > 0 && (
          <div className="product-review-card-container__content__imgs-wrapper">
            {productReview?.images?.map((image, index) => (
              <div
                key={image?.id_product_review_image}
                className="product-review-card-container__content__imgs-wrapper__img"
              >
                <ReviewImagesModal
                  id_product_review_image={image?.id_product_review_image}
                  segmentEvent={EVENT.reviewViewImageClicked}
                  triggerImageClassName="product-review-card-container__content__imgs-wrapper__img"
                  imageUrl={image?.image_url}
                  images={productReview?.images}
                >
                  <ReviewCarouselImages
                    selectedImageIndex={index}
                    images={productReview?.images}
                  />
                </ReviewImagesModal>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="product-review-card-container__content__title-description-wrapper">
        <div className={clsx(Typo.subtitle1)}>{productReview?.title}</div>
        <div className={clsx(Typo.body1)}>{productReview?.content}</div>
      </div>
    </div>
    <div className="product-review-card-container__divider" />

    <div className="product-review-card-container__rating">
      <div className="product-review-card-container__rating__inner-wrapper">
        <div className="product-review-card-container__rating__stars-wrapper">
          {productReview?.rating_product_as_display && (
            <div className="product-review-card-container__rating__stars-inner-wrapper">
              <div className={clsx(Typo.body2)}>
                {hasLocizeTranslation(
                  t,
                  'PRODUCT_AS_DISPLAYED',
                  'Product as Displayed',
                )}
                :
              </div>
              <AverageStars
                averageRating={productReview?.rating_product_as_display}
              />
            </div>
          )}
          {productReview?.rating_design_and_style && (
            <div className="product-review-card-container__rating__stars-inner-wrapper">
              <div className={clsx(Typo.body2)}>
                {hasLocizeTranslation(t, 'DESIGN_&_STYLE', 'Design & Style')}:
              </div>
              <AverageStars
                averageRating={productReview?.rating_design_and_style}
              />
            </div>
          )}
          {productReview?.rating_product_quality && (
            <div className="product-review-card-container__rating__stars-inner-wrapper">
              <div className={clsx(Typo.body2)}>
                {hasLocizeTranslation(t, 'QUALITY', 'Quality')}:
              </div>
              <AverageStars
                averageRating={productReview?.rating_product_quality}
              />
            </div>
          )}
        </div>
        <div className="product-review-card-container__rating__fit-size-wrapper">
          {productReview?.rating_true_to_size && (
            <div className="product-review-card-container__rating__fit-size-inner-wrapper">
              <div
                className={clsx(
                  Typo.body2,
                  'product-review-card-container__rating__fit-size-inner-wrapper__tag',
                )}
              >
                {hasLocizeTranslation(t, 'FIT', 'Fit')}:
              </div>
              <div className={clsx(Typo.body2)}>
                {getRatingFitTitleKey(t, productReview?.rating_true_to_size)}
              </div>
            </div>
          )}
          <div className="product-review-card-container__rating__fit-size-inner-wrapper">
            <div
              className={clsx(
                Typo.body2,
                'product-review-card-container__rating__fit-size-inner-wrapper__tag',
              )}
            >
              {hasLocizeTranslation(t, 'SIZE_ORDERED', 'Size Ordered')}:
            </div>
            <div className={clsx(Typo.body2)}>{productReview?.size}</div>
          </div>
        </div>
      </div>
      <div
        className={clsx(
          Typo.body2,
          'product-review-card-container__rating__date',
        )}
      >
        {productReview?.create_ts}
      </div>
    </div>
  </div>
)

ProductReviewCard.defaultProps = {
  productReview: {},
}

ProductReviewCard.propTypes = {
  // component props
  productReview: PropTypes.shape({
    id_product_review: PropTypes.number,
    average_rating: PropTypes.number,
    content: PropTypes.string,
    create_ts: PropTypes.string,
    firstname: PropTypes.string,
    rating_design_and_style: PropTypes.number,
    rating_product_as_display: PropTypes.number,
    rating_product_quality: PropTypes.number,
    rating_true_to_size: PropTypes.string,
    size: PropTypes.string,
    title: PropTypes.string,
    user_height: PropTypes.string,
    user_weight: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id_product_review_image: PropTypes.number,
        image_url: PropTypes.string,
      }),
    ),
  }),

  // redux props
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
  })),
  withI18next(),
)(ProductReviewCard)
